import { useQuery, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import type { Id } from "@convex/_generated/dataModel";
import type { ApplicationConfig } from "@repo/utils";
import { Breadcrumb, Button, Checkbox, Input, Label, PageContainer } from "@repo/ui";
import { ChoixApplications } from "../../components/ChoixApplications";
import { arianeOrganisation } from "../../lib/ariane";

export function GroupeForm() {
  const { id: orgId, groupId } = useParams<{ id: string; groupId?: string }>();
  const isEdit = !!groupId;
  const navigate = useNavigate();

  const org = useQuery(
    api.superAdmin.obtenirOrganisation,
    orgId ? { organisationId: orgId as Id<"organisations"> } : "skip",
  );

  const creer = useMutation(api.superAdmin.creerGroupe);
  const modifier = useMutation(api.superAdmin.modifierGroupe);

  const [nom, setNom] = useState("");
  const [restreindre, setRestreindre] = useState(false);
  const [applications, setApplications] = useState<ApplicationConfig["cle"][]>(
    [],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charge le groupe existant en cas de modification
  const groupes = useQuery(
    api.superAdmin.listerGroupes,
    orgId ? { organisationId: orgId as Id<"organisations"> } : "skip",
  );
  useEffect(() => {
    if (isEdit && groupes) {
      const g = groupes.find((g: any) => g._id === groupId);
      if (g) {
        setNom(g.nom);
        if (g.applicationsAutorisees) {
          setRestreindre(true);
          setApplications(g.applicationsAutorisees);
        }
      }
    }
  }, [groupes, isEdit, groupId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (isEdit) {
        await modifier({
          groupId: groupId as Id<"groups">,
          nom,
          applicationsAutorisees: restreindre ? applications : null,
        });
      } else {
        await creer({
          nom,
          organisationId: orgId as Id<"organisations">,
          applicationsAutorisees: restreindre ? applications : undefined,
        });
      }
      void navigate(`/organisations/${orgId}/groupes`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setSaving(false);
    }
  };

  const applicationsOrg = org?.applicationsAutorisees ?? [];
  const groupeExistant = groupes?.find((g) => g._id === groupId);

  return (
    <PageContainer>
      <Breadcrumb
        items={[
          ...arianeOrganisation(orgId, org?.nom, "groupes"),
          { label: isEdit ? (groupeExistant?.nom ?? "Groupe") : "Nouveau groupe" },
        ]}
      />
      <h1 className="text-2xl font-bold mb-6 text-center">
        {isEdit ? "Modifier le groupe" : "Nouveau groupe"}
      </h1>

      <form
        onSubmit={handleSubmit}
        className="space-y-5 bg-card border border-border rounded-surface p-6 max-w-xl mx-auto"
      >
        <div>
          <Label htmlFor="groupe-nom" className="mb-1">
            Nom *
          </Label>
          <Input
            id="groupe-nom"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            required
          />
        </div>

        <fieldset>
          <legend className="block text-sm font-medium">Applications</legend>
          <p className="text-xs text-muted-foreground mt-1 mb-3">
            Par défaut, le groupe a les applications de l'organisation.
          </p>
          {/* `htmlFor` plutôt qu'un <label> englobant : la Checkbox du design
              system est un bouton, pas un input natif — un label parent ne la
              basculerait pas au clic. */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="restreindre"
              checked={restreindre}
              onCheckedChange={(coche) => {
                setRestreindre(coche === true);
                if (coche === true && applications.length === 0) {
                  setApplications(applicationsOrg);
                }
              }}
            />
            <label htmlFor="restreindre" className="text-sm cursor-pointer">
              Restreindre ce groupe à une partie des applications
            </label>
          </div>

          {restreindre && (
            <div className="mt-3">
              <ChoixApplications
                idPrefix="application"
                proposees={applicationsOrg}
                value={applications}
                onChange={setApplications}
              />
            </div>
          )}
        </fieldset>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={saving || (restreindre && applications.length === 0)}
          >
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer"}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}
